import * as zod from 'zod';

import { validateRobotHardwareMapping } from '@sb/ai/helpers';
import {
  HardwareConfigEquipmentMapping,
  ParsedModelMetadataPayload,
} from '@sb/ai/types';
import type { HardwareConfigSchema } from '@sb/ai/types';
import { ActionRequiredError, Step } from '@sb/remote-control/types';

import { buildConditional } from '../util/conditionalsBuilder';

export namespace RunSkillStep {
  export const name = 'Run learned skill';
  export const description = 'Run an AI learned skill';
  export const librarySection = Step.LibrarySection.Control;
  export const argumentKind = 'RunSkill';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    modelPath: zod.string().nullable().default(null),
    timeout: zod.number().default(30),
    gpuTaskRunnerId: zod.string().nullable().default(null),
    isBimanual: zod.boolean().default(false),
    isPrimary: zod.boolean().default(true),
    equipmentKeyToEquipmentId: zod.record(zod.string()).default({}),
    hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping.default({}),
    condition: zod.string().nullable().default(null),
    modelMetadata: ParsedModelMetadataPayload.nullable().default(null),
    useLearnedCompletion: zod.boolean().default(false),
    learnedCompletionThreshold: zod.number().default(0.75),
    learnedCompletionConsecutiveCount: zod.number().int().default(2),
    useLearnedFailure: zod.boolean().default(false),
    learnedFailureThreshold: zod.number().default(0.25),
    learnedFailureConsecutiveCount: zod.number().int().default(3),
    useGripperMask: zod.boolean().default(true),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    equipment,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    if (!args.modelPath) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Model path not specified',
        fieldId: 'modelPath',
      });
    }

    if (args.gpuTaskRunnerId == null) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No GPU Task runner specified',
        fieldId: 'gpuTaskRunnerId',
      });
    }

    const equipmentConfig = equipment.find(
      (e) => e.id === args.gpuTaskRunnerId,
    );

    if (!equipmentConfig) {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: `Unable to find GPU Task Runner with id ${args.gpuTaskRunnerId} in equipment manager.`,
        fieldId: 'gpuTaskRunnerId',
      });
    }

    // Validate hardware configuration mapping before allowing routine to run
    if (args.isPrimary) {
      const robots: HardwareConfigSchema['robots'] = ((
        args.modelMetadata?.trainingRun?.modelArchitecture?.inputHardwareConfig
          ?.config as unknown as HardwareConfigSchema | undefined
      )?.robots ?? {}) as HardwareConfigSchema['robots'];

      const robotKeys = Object.keys(robots);

      // For single robot (non-bimanual): check primary robot only
      // For bimanual: check all robots (primary and secondary)
      const robotsToValidate = args.isBimanual
        ? robotKeys
        : robotKeys.filter((robotKey) => robots[robotKey].isPrimary);

      const unconfiguredRobots = robotsToValidate.filter((robotKey) => {
        const robotEquipmentMapping =
          args.hardwareConfigEquipmentMapping[robotKey];

        // If no mapping exists for this robot, it's unconfigured
        if (!robotEquipmentMapping) {
          return true;
        }

        return validateRobotHardwareMapping({
          robot: robots[robotKey],
          robotEquipmentMapping,
          botHardwareMap: args.modelMetadata?.botHardware,
        });
      });

      if (unconfiguredRobots.length > 0) {
        const inferenceType = args.isBimanual
          ? 'bimanual inference'
          : 'inference';

        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: `Hardware configuration mapping needs to be completed for all robot(s) before beginning ${inferenceType}`,
          fieldId: 'hardwareConfigEquipmentMapping',
        });
      }
    }

    if (args.timeout <= 0) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Timeout must be greater than 0',
        fieldId: 'timeout',
      });
    }

    const condition = args.condition
      ? buildConditional(JSON.parse(args.condition))
      : null;

    return {
      ...stepData,
      stepKind: 'RunSkill',
      args: {
        gpuTaskRunnerId: args.gpuTaskRunnerId,
        modelPath: args.modelPath,
        timeout: args.timeout,
        isBimanual: args.isBimanual,
        isPrimary: args.isPrimary,
        equipmentKeyToEquipmentId: args.equipmentKeyToEquipmentId,
        hardwareConfigEquipmentMapping: args.hardwareConfigEquipmentMapping,
        condition,
        useLearnedCompletion: args.useLearnedCompletion,
        learnedCompletionThreshold: args.learnedCompletionThreshold,
        learnedCompletionConsecutiveCount:
          args.learnedCompletionConsecutiveCount,
        useLearnedFailure: args.useLearnedFailure,
        learnedFailureThreshold: args.learnedFailureThreshold,
        learnedFailureConsecutiveCount: args.learnedFailureConsecutiveCount,
        useGripperMask: args.useGripperMask,
      },
    };
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
  }) => {
    if (args?.argumentKind !== argumentKind) {
      return null;
    }

    const modelName = args?.modelMetadata?.trainingRun?.name;

    if (!(args.modelPath || modelName)) {
      return 'with a model';
    }

    if (modelName) {
      return `with model "${modelName}"`;
    }

    const modelPathParts = args.modelPath?.split('/') || [];

    if (modelPathParts.length === 0) {
      return 'with a model';
    }

    return `with model "${modelPathParts[modelPathParts.length - 1]}"`;
  };
}

RunSkillStep satisfies Step.StepKindInfo;
